import axios from 'axios';
import { createContext, useContext, useEffect, useRef } from 'react';
import { useSupabase } from '../supabaseAuth';

// Create API context
const ApiContext = createContext();

// Get API URL from environment variable or default to dev
const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

// Create axios instance
const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to log full URLs
api.interceptors.request.use(
  config => {
    // console.log('Full request URL:', config.baseURL + config.url);
    return config;
  },
  error => Promise.reject(error)
);

// API service object
const apiService = {
  // Auth-related API calls for audit logging
  logLogin: async (userData) => {
    // console.log('Calling backend login endpoint for audit logging');
    try {
      // Add a flag to indicate this is a true login event, not a session recovery
      const payload = {
        ...userData,
        is_true_login: true
      };
      
      const response = await api.post('/api/login', payload);
      // console.log('Login audit log created successfully');
      return response;
    } catch (error) {
      console.error('Error creating login audit log:', error);
      // Don't throw error to prevent disrupting the main auth flow
      return { error };
    }
  },
  
  logLogout: async () => {
    // console.log('Calling backend logout endpoint for audit logging');
    try {
      const response = await api.post('/api/logout');
      // console.log('Logout audit log created successfully');
      return response;
    } catch (error) {
      console.error('Error creating logout audit log:', error);
      // Don't throw error to prevent disrupting the main auth flow
      return { error };
    }
  },
  
  // Create a new subscription
  createSubscription: async (subscriptionData) => {
    return await api.post('/api/subscriptions', subscriptionData);
  },
  
  // Create payment intent for direct subscription
  createPaymentIntent: async (subscriptionData) => {
    return await api.post('/api/stripe/create-payment-intent', subscriptionData);
  },
  
  // Complete subscription after payment
  completeSubscription: async (data) => {
    return await api.post('/api/stripe/complete-subscription', data);
  },

  // Create subscription with payment method validation (NEW)
  createSubscriptionWithValidation: async (subscriptionData) => {
    // console.log('Calling createSubscriptionWithValidation API endpoint with data:', subscriptionData);
    try {
      const response = await api.post('/api/stripe/create-subscription-with-validation', subscriptionData);
      // console.log('Create subscription with validation API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error creating subscription with validation:', error);
      throw error;
    }
  },

  // Create subscription with payment method (for wizard flow)
  createSubscriptionWithPaymentMethod: async (subscriptionData) => {
    // console.log('Calling createSubscriptionWithPaymentMethod API endpoint with data:', subscriptionData);
    try {
      const response = await api.post('/api/stripe/create-subscription-with-payment-method', subscriptionData);
      // console.log('Create subscription with payment method API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error creating subscription with payment method:', error);
      throw error;
    }
  },

  // Validate and save payment method with $1 preauth
  validateAndSavePaymentMethod: async (paymentData) => {
    // console.log('Calling validateAndSavePaymentMethod API endpoint with data:', paymentData);
    try {
      const response = await api.post('/api/stripe/validate-and-save-payment-method', paymentData);
      // console.log('Validate and save payment method API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error validating and saving payment method:', error);
      throw error;
    }
  },
  
  // Create a setup intent for saving payment methods
  createSetupIntent: async () => {
    return await api.post('/api/stripe/create-setup-intent');
  },

  // Get user's saved payment methods
  getPaymentMethods: async () => {
    return await api.get('/api/stripe/payment-methods');
  },
  
  // Create a Stripe customer with payment method
  createStripeCustomer: async (data) => {
    return await api.post('/api/stripe/create-customer', data);
  },

  // Set a payment method as default
  setDefaultPaymentMethod: async (data) => {
    return await api.post('/api/stripe/update-default-payment-method', data);
  },

  // Delete a payment method
  deletePaymentMethod: async (paymentMethodId) => {
    return await api.delete(`/api/stripe/payment-methods/${paymentMethodId}`);
  },

  // User addresses
  getUserAddresses: async () => {
    return await api.get('/api/account/addresses');
  },

  createUserAddress: async (data) => {
    return await api.post('/api/account/addresses', data);
  },

  updateUserAddress: async (addressId, data) => {
    return await api.put(`/api/account/addresses/${addressId}`, data);
  },

  deleteUserAddress: async (addressId) => {
    return await api.delete(`/api/account/addresses/${addressId}`);
  },

  setDefaultAddress: async (addressId) => {
    return await api.post(`/api/account/addresses/${addressId}/set-default`, {});
  },
  
  // Email preferences methods
  getEmailPreferences: async () => {
    // console.log('Calling getEmailPreferences API endpoint');
    try {
      const response = await api.get('/api/account/email-preferences');
      // console.log('Email preferences API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching email preferences:', error);
      throw error;
    }
  },
  
  updateEmailPreferences: async (preferences) => {
    // console.log('Calling updateEmailPreferences API endpoint with data:', preferences);
    try {
      const response = await api.post('/api/account/email-preferences', preferences);
      // console.log('Update email preferences API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error updating email preferences:', error);
      throw error;
    }
  },
  
  // Get metal prices - with enhanced logging and error handling
  getMetalPrices: async () => {
    // console.log('Calling getMetalPrices API endpoint');
    try {
      const response = await api.get('/api/metal-prices');
      // console.log('Metal prices API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching metal prices:', error);
      // Return a default structure to prevent UI errors
      return {
        data: {
          current: {
            gold: null,
            silver: null,
            platinum: null
          }
        }
      };
    }
  },
  
  // Alias for getMetalPrices to maintain compatibility with components
  fetchMetalPrices: async () => {
    // console.log('Calling fetchMetalPrices (alias for getMetalPrices)');
    try {
      const response = await api.get('/api/metal-prices');
      // console.log('Metal prices API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching metal prices:', error);
      // Return a default structure to prevent UI errors
      return {
        data: {
          current: {
            gold: null,
            silver: null,
            platinum: null
          }
        }
      };
    }
  },
  
  // Get user transactions with optional filters and pagination
  getUserTransactions: async (params = {}) => {
    // console.log('Calling getUserTransactions API endpoint with params:', params);
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters if provided
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filter parameters if provided
      if (params.type) queryParams.append('type', params.type);
      if (params.status) queryParams.append('status', params.status);
      if (params.subscriptionId) queryParams.append('subscription_id', params.subscriptionId);
      
      // Build URL with query parameters
      const url = `/api/transactions${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await api.get(url);
      // console.log('Transactions API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  },
  
  // Get transaction details by ID
  getTransactionDetails: async (id) => {
    // console.log(`Calling getTransactionDetails API endpoint for transaction ${id}`);
    try {
      const response = await api.get(`/api/transactions/${id}`);
      // console.log('Transaction details API response:', response.data);
      return response;
    } catch (error) {
      console.error(`Error fetching transaction ${id}:`, error);
      throw error;
    }
  },
  
  // Original API methods
  getSubscriptions: async () => {
    // Debug token before request
    const token = localStorage.getItem('sb-access-token');
    // console.log('Token before getSubscriptions request:', token ? 'Present' : 'Missing');
    
    // Explicitly set token for this request
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // console.log('Explicitly set Authorization header for getSubscriptions');
    }
    
    try {
      const response = await api.get('/api/subscriptions');
      // console.log('Subscriptions API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  },
  
  // Pause a subscription
  pauseSubscription: async (id) => {
    // console.log(`Calling pauseSubscription API endpoint for subscription ${id}`);
    try {
      const response = await api.post(`/api/subscriptions/${id}/pause`);
      // console.log('Pause subscription API response:', response.data);
      return response;
    } catch (error) {
      console.error(`Error pausing subscription ${id}:`, error);
      throw error;
    }
  },
  
  // Resume a paused subscription
  resumeSubscription: async (id) => {
    // console.log(`Calling resumeSubscription API endpoint for subscription ${id}`);
    try {
      const response = await api.post(`/api/subscriptions/${id}/resume`);
      // console.log('Resume subscription API response:', response.data);
      return response;
    } catch (error) {
      console.error(`Error resuming subscription ${id}:`, error);
      throw error;
    }
  },
  
  // Cancel a subscription
  cancelSubscription: async (id) => {
    // console.log(`Calling cancelSubscription API endpoint for subscription ${id}`);
    try {
      const response = await api.post(`/api/subscriptions/${id}/cancel`);
      // console.log('Cancel subscription API response:', response.data);
      return response;
    } catch (error) {
      console.error(`Error cancelling subscription ${id}:`, error);
      throw error;
    }
  },
  
  // Get subscription plans
  getSubscriptionPlans: async () => {
    // console.log('Calling getSubscriptionPlans API endpoint');
    try {
      const response = await api.get('/api/subscriptions/weight-options');
      // console.log('Subscription plans API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  },
  
  // Get subscription details by ID
  getSubscriptionDetails: async (id) => {
    // console.log(`Calling getSubscriptionDetails API endpoint for subscription ${id}`);
    try {
      const response = await api.get(`/api/subscriptions/${id}`);
      // console.log('Subscription details API response:', response.data);
      return response;
    } catch (error) {
      console.error(`Error fetching subscription ${id}:`, error);
      throw error;
    }
  },
  
  // Get subscription balance (placeholder - not implemented in backend)
  getSubscriptionBalance: async (id) => {
    // console.log(`Calling getSubscriptionBalance API endpoint for subscription ${id}`);
    try {
      const response = await api.get(`/api/subscriptions/${id}`);
      // console.log('Subscription balance API response:', response.data);
      // Return balance from subscription details
      return { data: { balance: response.data.current_balance } };
    } catch (error) {
      console.error(`Error fetching subscription balance ${id}:`, error);
      throw error;
    }
  },
  
  // Update subscription
  updateSubscription: async (id, updates) => {
    // console.log(`Calling updateSubscription API endpoint for subscription ${id}`, updates);
    try {
      const response = await api.put(`/api/subscriptions/${id}`, updates);
      // console.log('Update subscription API response:', response.data);
      return response;
    } catch (error) {
      console.error(`Error updating subscription ${id}:`, error);
      throw error;
    }
  },
  
  // Create payment intent for subscription
  createPaymentIntent: async (subscriptionData) => {
    // console.log('Calling createPaymentIntent API endpoint', subscriptionData);
    try {
      const response = await api.post('/api/stripe/create-payment-intent', subscriptionData);
      // console.log('Create payment intent API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  },
  
  // Complete subscription after payment
  completeSubscription: async (data) => {
    // console.log('Calling completeSubscription API endpoint', data);
    try {
      const response = await api.post('/api/stripe/complete-subscription', data);
      // console.log('Complete subscription API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error completing subscription:', error);
      throw error;
    }
  },
  
  getOrders: async () => {
    try {
      const response = await api.get('/api/orders');
      return response;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },
  
  createOrder: async (orderData) => {
    try {
      const response = await api.post('/api/orders', orderData);
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },
  
  updateOrder: async (orderId, orderData) => {
    try {
      const response = await api.put(`/api/orders/${orderId}`, orderData);
      return response;
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  },
  
  deleteOrder: async (orderId) => {
    try {
      const response = await api.delete(`/api/orders/${orderId}`);
      return response;
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  },
  
  // Cart-related API calls
  addToCart: async (item) => {
    try {
      const response = await api.post('/api/cart', item);
      return response;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  },
  
  getCart: async () => {
    try {
      const response = await api.get('/api/cart');
      return response;
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  },
  
  updateCartItem: async (itemId, quantity) => {
    try {
      const response = await api.put(`/api/cart/${itemId}`, { quantity });
      return response;
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  },
  
  removeFromCart: async (itemId) => {
    try {
      const response = await api.delete(`/api/cart/${itemId}`);
      return response;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  },
  
  clearCart: async () => {
    try {
      const response = await api.delete('/api/cart');
      return response;
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  },
  
  // Admin API calls
  getUsers: async () => {
    try {
      const response = await api.get('/api/admin/users');
      return response;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },
  
  getAdminSubscriptions: async () => {
    try {
      const response = await api.get('/api/admin/subscriptions');
      return response;
    } catch (error) {
      console.error('Error fetching admin subscriptions:', error);
      throw error;
    }
  },
  
  getAuditLogs: async () => {
    try {
      const response = await api.get('/api/admin/audit');
      return response;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },
  
  // Set auth token in API headers
  setAuthToken: (token) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('sb-access-token', token);
    }
  },
  
  // Clear auth token from API headers
  clearAuthToken: () => {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('sb-access-token');
  }
};

// API Provider component
export const ApiProvider = ({ children }) => {
  const { getAccessToken, isAuthenticated, session, supabase } = useSupabase();
  const interceptorRef = useRef(null);
  
  // Set up token immediately on mount
  useEffect(() => {
    const setupInitialToken = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessToken();
          if (token) {
            // console.log('Initial token setup complete');
            // Force a direct axios default header update as a fallback
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            // Also update our api instance headers
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            // console.log('Authorization header set on both axios and api instance');
          } else {
            // console.warn('No token available during initial setup');
          }
        } catch (error) {
          console.error('Error during initial token setup:', error);
        }
      }
    };
    
    setupInitialToken();
  }, [isAuthenticated, getAccessToken]);
  
  // Update request interceptor whenever session changes
  useEffect(() => {
    console.log('Setting up API interceptor, auth state:', isAuthenticated ? 'authenticated' : 'not authenticated');
    
    // Clean up previous interceptor if it exists
    if (interceptorRef.current !== null) {
      api.interceptors.request.eject(interceptorRef.current);
      console.log('Previous interceptor cleaned up');
    }
    
    // Set up new interceptor
    interceptorRef.current = api.interceptors.request.use(
      async (config) => {
        // Only add token for authenticated requests
        if (isAuthenticated) {
          try {
            const token = await getAccessToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            console.error('Error getting access token in interceptor:', error);
          }
        }
        
        // Log request details (but redact sensitive info)
        console.log('API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          Authorization: config.headers.Authorization ? 'Bearer [REDACTED]' : 'none'
        });
        
        // Force HTTPS for all requests
        if (config.url && config.baseURL) {
          const fullUrl = config.baseURL + config.url;
          if (fullUrl.startsWith('http:')) {
            config.baseURL = config.baseURL.replace('http:', 'https:');
            console.log('Forced HTTPS upgrade for URL:', config.baseURL + config.url);
          }
        }
        
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );
    
    console.log('New interceptor set up with ID:', interceptorRef.current);
    
    return () => {
      // Clean up interceptor when component unmounts or session changes
      if (interceptorRef.current !== null) {
        api.interceptors.request.eject(interceptorRef.current);
        interceptorRef.current = null;
        console.log('Interceptor cleaned up on unmount/change');
      }
    };
  }, [getAccessToken, isAuthenticated, session, supabase]);
  
  return (
    <ApiContext.Provider value={apiService}>
      {children}
    </ApiContext.Provider>
  );
};

// Custom hook to use the API context
export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

// Export the axios instance for direct use if needed
export { api };

// Export apiService as default for backward compatibility
export default apiService;
